import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { Button, Segment } from "semantic-ui-react";
import { toast } from "../..";
import { UserRole } from "../../actions/authentificationActions";
import { IStatisticsResult, statisticsInit } from "../../models/statistics";
import {
    exportCertificateStatistics,
    getStatistics,
    getCurrentActiveCertificates,
} from "../../api/statistics";
import { AppState } from "../../store/configureStore";
import { StatisticsTable } from "./StatisticsTable";
import { CalendarInputDouble } from "../../components/CalendarInput/CalendarInputDouble";
import {
    formatDateRange,
    formatDateRangeDisplay,
} from "../../components/CalendarInput/dateHelpers";
import { constructDate } from "../../models/common";
import "./Statistics.css";

export const Statistics = () => {
    const user = useSelector((state: AppState) => state.user);
    const [statistics, setStatistics] = useState<IStatisticsResult>();
    const [currentActiveCertificates, setCurrentActiveCertificates] = useState(statisticsInit);
    const [dates, setDates] = useState<Date[]>([]);
    const [exporting, setExporting] = useState(false);
    const intl = useIntl();

    const changePeriod = (e: any, manualInput: boolean) => {
        !manualInput ? setDates(formatDateRange(e)) : setDates(e);
    };

    const fetchCurrentActiveCertificates = useCallback(async () => {
        try {
            const res = await getCurrentActiveCertificates();
            setCurrentActiveCertificates(res);
        } catch {
            toast(intl.formatMessage({ id: "unableToFetchStatistics" }), false);
        }
    }, [intl]);

    const fetchStatistics = useCallback(async () => {
        setStatistics(undefined);
        await new Promise(r => setTimeout(r, 100));
        try {
            const startDate = constructDate(dates[0]);
            const endDate = constructDate(dates[1]);
            const res = await getStatistics(startDate, endDate);
            setStatistics(res);
        } catch {
            toast(intl.formatMessage({ id: "unableToFetchStatistics" }), false);
        }
    }, [intl, dates]);

    const exportStatistics = async () => {
        setExporting(true);
        try {
            const startDate = constructDate(dates[0]);
            const endDate = constructDate(dates[1]);
            await exportCertificateStatistics(startDate, endDate);
        } catch {
            toast(intl.formatMessage({ id: "failedToExportStatistics" }), false);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchCurrentActiveCertificates();
    }, [fetchCurrentActiveCertificates]);

    useEffect(() => {
        dates.length > 0 && fetchStatistics();
    }, [fetchStatistics, dates]);

    return user.role !== UserRole.PowerUser && user.role !== UserRole.OfficeUser ? (
        <Redirect to="/unauthorized" />
    ) : (
        <div className="statistics-page-container">
            <div className="container-header">
                <h1>{intl.formatMessage({ id: "statistics" })}</h1>
                <Button
                    primary
                    content={intl.formatMessage({ id: "exportStatistics" })}
                    disabled={exporting || !statistics || dates.length === 0}
                    loading={exporting}
                    onClick={() => exportStatistics()}
                />
            </div>
            <div className="statistics-container">
                <Segment className="grey-segment search-segment">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "period" })}</label>
                        <CalendarInputDouble
                            dates={dates}
                            handleDateChange={(e: any, manualInput: boolean) =>
                                changePeriod(e, manualInput)
                            }
                        />
                    </section>
                </Segment>
                <div className="statistics-grid">
                    <StatisticsTable
                        title={intl.formatMessage({ id: "currentActiveCertificates" })}
                        statistics={currentActiveCertificates}
                        period=""
                    />
                    <CSSTransition in={!!statistics} timeout={100} classNames="fade" unmountOnExit>
                        <>
                            {statistics && (
                                <>
                                    <StatisticsTable
                                        title={intl.formatMessage({ id: "issuedCertificates" })}
                                        statistics={statistics.issuedCertificates}
                                        period={formatDateRangeDisplay(dates)}
                                    />
                                    <StatisticsTable
                                        title={intl.formatMessage({ id: "suspendedCertificates" })}
                                        statistics={statistics.suspendedCertificates}
                                        period={formatDateRangeDisplay(dates)}
                                    />
                                    <StatisticsTable
                                        title={intl.formatMessage({ id: "withdrawnCertificates" })}
                                        statistics={statistics.withdrawnCertificates}
                                        period={formatDateRangeDisplay(dates)}
                                    />
                                </>
                            )}
                        </>
                    </CSSTransition>
                </div>
            </div>
        </div>
    );
};
